import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme as useMuiTheme,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  EmojiEvents as BadgeIcon,
  WorkspacePremium as CertificateIcon,
  Leaderboard as LeaderboardIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as AutoModeIcon,
  Help as HelpIcon,
  CloudUpload as ImportIcon,
  Groups as UsersIcon,
  Assignment as AssignmentIcon,
  NewReleases as ReleaseNotesIcon,
  MenuBook as AdminCoursesIcon,
  ViewModule as ModulesIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import HelpMenu from '../components/HelpMenu';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const DRAWER_WIDTH = 240;

// Routes where sidebar should be collapsed by default (lesson and module views)
const SIDEBAR_COLLAPSED_ROUTES = ['/lessons/', '/modules/'];

const menuItems = [
  { path: '/dashboard', labelKey: 'nav.dashboard', icon: <DashboardIcon /> },
  { path: '/courses', labelKey: 'nav.courses', icon: <SchoolIcon /> },
  { path: '/badges', labelKey: 'nav.badges', icon: <BadgeIcon /> },
  { path: '/certificates', labelKey: 'nav.certificates', icon: <CertificateIcon /> },
  { path: '/leaderboard', labelKey: 'nav.leaderboard', icon: <LeaderboardIcon /> },
];

const adminMenuItems = [
  { path: '/admin', labelKey: 'admin.dashboard', icon: <DashboardIcon /> },
  { path: '/admin/users', labelKey: 'admin.users', icon: <UsersIcon /> },
  { path: '/admin/courses', labelKey: 'admin.courses', icon: <AdminCoursesIcon /> },
  { path: '/admin/modules', labelKey: 'admin.modules', icon: <ModulesIcon /> },
  { path: '/admin/assignments', labelKey: 'admin.assignments', icon: <AssignmentIcon /> },
  { path: '/admin/import', labelKey: 'admin.import.title', icon: <ImportIcon /> },
  { path: '/admin/release-notes', labelKey: 'admin.releaseNotes', icon: <ReleaseNotesIcon /> },
];

function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout, hasRole } = useAuth();
  const isAdmin = hasRole('admin') || hasRole('content-manager');
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(() => {
    // Check if current route should have sidebar collapsed by default
    const shouldCollapse = SIDEBAR_COLLAPSED_ROUTES.some(route => location.pathname.includes(route));
    return !shouldCollapse;
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<null | HTMLElement>(null);

  // Auto-collapse sidebar when entering lesson/module routes
  useEffect(() => {
    const shouldCollapse = SIDEBAR_COLLAPSED_ROUTES.some(route => location.pathname.includes(route));
    if (shouldCollapse && !isMobile) {
      setDesktopSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopSidebarOpen(!desktopSidebarOpen);
    }
  };
  const handleDrawerOpen = () => setMobileOpen(true);
  const handleDrawerClose = () => setMobileOpen(false);

  // Swipe handler for opening drawer from left edge
  const swipeHandlers = useSwipeable({
    onSwipedRight: (eventData) => {
      // Only open if swipe started near the left edge
      if (eventData.initial[0] < 30 && isMobile) {
        handleDrawerOpen();
      }
    },
    trackMouse: false,
    delta: 50,
  });
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);
  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => setHelpMenuAnchor(event.currentTarget);
  const handleHelpMenuClose = () => setHelpMenuAnchor(null);
  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAdminMenuAnchor(event.currentTarget);
  const handleAdminMenuClose = () => setAdminMenuAnchor(null);

  const getThemeIcon = () => {
    switch (mode) {
      case 'light':
        return <LightModeIcon />;
      case 'dark':
        return <DarkModeIcon />;
      default:
        return <AutoModeIcon />;
    }
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Box
          component="img"
          src="/learn-logo.png"
          alt="Learn"
          sx={{
            height: 40,
            cursor: 'pointer',
            objectFit: 'contain',
          }}
          onClick={() => window.location.href = '/dashboard'}
        />
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                if (isMobile) setMobileOpen(false);
                window.location.href = item.path;
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={t(item.labelKey)} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: desktopSidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          ml: { md: desktopSidebarOpen ? `${DRAWER_WIDTH}px` : 0 },
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1,
          transition: muiTheme.transitions.create(['width', 'margin'], {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* Admin Menu */}
          {isAdmin && (
            <Tooltip title={t('nav.admin')}>
              <IconButton
                onClick={handleAdminMenuOpen}
                color={location.pathname.startsWith('/admin') ? 'primary' : 'inherit'}
                sx={{ mr: 1 }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          )}

          <IconButton onClick={toggleTheme} color="inherit" title={`Theme: ${mode}`}>
            {getThemeIcon()}
          </IconButton>

          <LanguageSwitcher />

          <IconButton onClick={handleHelpMenuOpen} color="inherit">
            <HelpIcon />
          </IconButton>

          <IconButton onClick={handleUserMenuOpen} sx={{ ml: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              {user?.name?.charAt(0) || user?.username?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: desktopSidebarOpen ? DRAWER_WIDTH : 0 }, flexShrink: { md: 0 }, transition: muiTheme.transitions.create('width', { easing: muiTheme.transitions.easing.sharp, duration: muiTheme.transitions.duration.leavingScreen }) }}>
        {/* Mobile SwipeableDrawer */}
        <SwipeableDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          onOpen={handleDrawerOpen}
          ModalProps={{ keepMounted: true }}
          disableBackdropTransition
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              // Safe area insets for notch devices
              paddingTop: 'env(safe-area-inset-top)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            },
          }}
        >
          {drawer}
        </SwipeableDrawer>
        {/* Desktop permanent Drawer */}
        {desktopSidebarOpen && (
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: DRAWER_WIDTH },
            }}
            open
          >
            {drawer}
          </Drawer>
        )}
      </Box>

      <Box
        {...(isMobile ? swipeHandlers : {})}
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: desktopSidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          mt: 8,
          mb: { xs: 8, md: 0 }, // Space for mobile bottom nav
          pb: { xs: 'calc(env(safe-area-inset-bottom) + 64px)', md: 0 }, // Safe area + bottom nav
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: muiTheme.transitions.create('width', {
            easing: muiTheme.transitions.easing.sharp,
            duration: muiTheme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Outlet />
      </Box>

      {/* Mobile Bottom Navigation - Touch-optimized */}
      {isMobile && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-around',
            py: 0.5,
            pb: 'calc(env(safe-area-inset-bottom) + 4px)', // Safe area for notch devices
            zIndex: muiTheme.zIndex.appBar,
          }}
        >
          {[
            { path: '/dashboard', icon: <DashboardIcon />, label: t('nav.dashboard') },
            { path: '/courses', icon: <SchoolIcon />, label: t('nav.courses') },
            { path: '/badges', icon: <BadgeIcon />, label: t('nav.badges') },
            { path: '/profile', icon: <PersonIcon />, label: t('nav.profile') },
          ].map((item) => (
            <Box
              key={item.path}
              onClick={() => window.location.href = item.path}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                minWidth: 64,
                minHeight: 56, // Touch-friendly height (min 44px)
                py: 0.5,
                px: 1,
                borderRadius: 1,
                transition: 'all 0.15s',
                '&:active': {
                  transform: 'scale(0.95)',
                  bgcolor: 'action.hover',
                },
              }}
            >
              {item.icon}
              <Typography variant="caption" sx={{ mt: 0.25, fontSize: '0.65rem' }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => window.location.href = '/profile'}>
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('nav.profile')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => window.location.href = '/settings'}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('nav.settings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={logout}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText>{t('nav.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Help Menu */}
      <HelpMenu anchorEl={helpMenuAnchor} open={Boolean(helpMenuAnchor)} onClose={handleHelpMenuClose} />

      {/* Admin Menu */}
      <Menu
        anchorEl={adminMenuAnchor}
        open={Boolean(adminMenuAnchor)}
        onClose={handleAdminMenuClose}
        onClick={handleAdminMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {adminMenuItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => window.location.href = item.path}
            selected={location.pathname === item.path}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{t(item.labelKey)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

export default MainLayout;
